/* eslint-disable no-underscore-dangle */
import { ExtendedUserDTO } from 'src/modules/generated/api';
import { notificationLocalStorageKey } from 'src/modules/global-vars';

import { AppNotification, NotificationStorageItem } from './types';

export function readNotificationStorage(): NotificationStorageItem[] {
  const storage = localStorage.getItem(notificationLocalStorageKey);
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    localStorage.removeItem(notificationLocalStorageKey);
    return [];
  }
}

export function writeNotificationStorage(items: NotificationStorageItem[]) {
  localStorage.setItem(notificationLocalStorageKey, JSON.stringify(items));
}

/**
 * Latest editor-set update time (own or inherited from the base language document) that has
 * already been reached. Future values are ignored until they arrive, so a scheduled update
 * neither resurfaces the notification early nor gets swallowed by an early read.
 */
export function getEffectiveUpdatedAt(notification: AppNotification, now: Date = new Date()): Date | undefined {
  return [notification.updatedAt, notification.baseUpdatedAt]
    .filter((value): value is string => !!value)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()) && date <= now)
    .sort((a, b) => b.getTime() - a.getTime())[0];
}

export function getReferenceDate(notification: AppNotification, now: Date = new Date()): Date {
  return getEffectiveUpdatedAt(notification, now) ?? new Date(notification.startTime || notification.added);
}

function cleanNotificationStorage() {
  if (!localStorage.getItem(notificationLocalStorageKey)) return;
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  writeNotificationStorage(readNotificationStorage().filter((item) => new Date(item.date) >= oneYearAgo));
}

export function filterNotifications(
  notifications: AppNotification[] | undefined,
  user: ExtendedUserDTO | undefined,
  onlyUnread: boolean = true,
) {
  cleanNotificationStorage();
  const storage = readNotificationStorage();
  const now = new Date();

  let messages = (notifications || [])
    .map((notification) => {
      // Read if stored, unless an editor update was reached after the stored read time
      const storageItem = storage.find((item) => item.id === notification._id);
      const effectiveUpdatedAt = getEffectiveUpdatedAt(notification, now);
      const isRead = !!storageItem && (!effectiveUpdatedAt || effectiveUpdatedAt <= new Date(storageItem.date));
      return { ...notification, isRead };
    })
    .filter((notification) => {
      if (notification.startTime && new Date(notification.startTime) > now) return false;
      if (notification.endTime && new Date(notification.endTime) < now) return false;
      if (onlyUnread && notification.isRead) return false;
      return true;
    });

  // Filter out notifications based on user roles
  if (user && user.roles) {
    messages = messages.filter((notification) => {
      if (!notification.roles || notification.roles.length === 0) return true;
      return notification.roles.some((role) => user.roles?.includes(role));
    });
  }
  // Filter out Messages based on countries
  if (user && user.dealer?.country) {
    messages = messages.filter((notification) => {
      if (!notification.countries || notification.countries.length === 0) return true;
      return notification.countries.includes(user.dealer?.country || '');
    });
  }
  return messages.sort((a, b) => getReferenceDate(b, now).getTime() - getReferenceDate(a, now).getTime());
}
